/**
 * AdminLayout — shell for admin panel pages.
 * Similar to DashboardLayout but for administrative content.
 * Responsive: sidebar collapses to a slide-over on mobile.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { LayoutSlot } from '@/components/layout';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useScrollLock } from '@/hooks/useScrollLock';

export function AdminLayout(): JSX.Element {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  useFocusTrap(mobileNavRef, mobileNavOpen);
  useScrollLock(mobileNavOpen);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileNavOpen, closeMobileNav]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary-700 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to main content
      </a>

      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border md:block" aria-label="Admin navigation">
        <div className="flex h-16 items-center border-b border-border px-4">
          <LayoutSlot name="Admin Sidebar Header" />
        </div>
        <nav className="flex flex-col gap-1 p-4" aria-label="Admin menu">
          <LayoutSlot name="Admin Sidebar Navigation" />
        </nav>
      </aside>

      {/* Mobile sidebar */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Admin navigation">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobileNav} />
          <div
            ref={mobileNavRef}
            className="absolute left-0 top-0 h-full w-64 bg-background shadow-xl"
          >
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <LayoutSlot name="Admin Sidebar Header" />
              <button
                type="button"
                onClick={closeMobileNav}
                className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                aria-label="Close navigation menu"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 p-4" aria-label="Admin menu" onClick={closeMobileNav}>
              <LayoutSlot name="Admin Sidebar Navigation" />
            </nav>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center border-b border-border px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30 md:hidden"
            aria-label="Open navigation menu"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <LayoutSlot name="Admin Top Bar" />
        </header>
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
